import React, { useState, useEffect, useCallback } from "react";
import { companyConfig } from "../config/company";
import "./CookieConsent.css";

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [showDetails, setShowDetails] = useState<boolean>(false);
  const [settings, setSettings] = useState<CookieSettings>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  const setCookie = useCallback((name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Strict`;
  }, []);

  const deleteCookie = useCallback((name: string) => {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
  }, []);

  const applyCookieSettings = useCallback(
    (selectedSettings: CookieSettings) => {
      // Notwendige Cookies immer setzen
      setCookie("necessary", "true", 365);

      // Analytics Cookies
      if (selectedSettings.analytics) {
        setCookie("analytics_enabled", "true", 365);
        // Hier können Sie Analytics-Tracking initialisieren
        // Beispiel: initializeAnalytics();
      } else {
        deleteCookie("analytics_enabled");
        // Hier können Sie Analytics-Tracking deaktivieren
        // Beispiel: disableAnalytics();
      }

      // Marketing Cookies
      if (selectedSettings.marketing) {
        setCookie("marketing_enabled", "true", 365);
        // Hier können Sie Marketing-Tracking initialisieren
        // Beispiel: initializeMarketing();
      } else {
        deleteCookie("marketing_enabled");
        // Hier können Sie Marketing-Tracking deaktivieren
        // Beispiel: disableMarketing();
      }
    },
    [setCookie, deleteCookie]
  );

  useEffect(() => {
    const consent = localStorage.getItem("cookieConsent");
    if (!consent) {
      setIsVisible(true);
    } else {
      // Wenn Einwilligung vorliegt, Cookies entsprechend setzen
      const savedSettings = JSON.parse(consent);
      setSettings(savedSettings);
      applyCookieSettings(savedSettings);
    }
  }, [applyCookieSettings]);

  const handleAcceptAll = () => {
    const newSettings = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    saveConsent(newSettings);
  };

  const handleSaveSelection = () => {
    saveConsent(settings);
  };

  const saveConsent = (selectedSettings: CookieSettings) => {
    localStorage.setItem("cookieConsent", JSON.stringify(selectedSettings));
    applyCookieSettings(selectedSettings);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="cookie-consent">
      <div className="cookie-content">
        <h2>Datenschutzeinstellungen</h2>
        <p>
          Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer
          Website zu bieten. Hier können Sie Ihre Einstellungen verwalten.
          Weitere Informationen finden Sie in unserer{" "}
          <a href="/datenschutz">Datenschutzerklärung</a>.
        </p>

        {showDetails && (
          <div className="cookie-settings">
            <div className="cookie-option">
              <label>
                <input type="checkbox" checked={settings.necessary} disabled />
                <span>Notwendige Cookies</span>
              </label>
              <p>
                Diese Cookies sind für die Grundfunktionen der Website von{" "}
                {companyConfig.name}
                erforderlich.
              </p>
            </div>

            <div className="cookie-option">
              <label>
                <input
                  type="checkbox"
                  checked={settings.analytics}
                  onChange={(e) =>
                    setSettings({ ...settings, analytics: e.target.checked })
                  }
                />
                <span>Analyse Cookies</span>
              </label>
              <p>
                Diese Cookies helfen uns, die Nutzung der Website zu verstehen
                und zu verbessern.
              </p>
            </div>

            <div className="cookie-option">
              <label>
                <input
                  type="checkbox"
                  checked={settings.marketing}
                  onChange={(e) =>
                    setSettings({ ...settings, marketing: e.target.checked })
                  }
                />
                <span>Marketing Cookies</span>
              </label>
              <p>
                Diese Cookies werden verwendet, um Werbung relevanter für Sie zu
                machen.
              </p>
            </div>
          </div>
        )}

        <div className="cookie-actions">
          <button
            className="btn-secondary"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails
              ? "Einstellungen ausblenden"
              : "Einstellungen anzeigen"}
          </button>

          {showDetails ? (
            <button className="btn-primary" onClick={handleSaveSelection}>
              Auswahl speichern
            </button>
          ) : (
            <button className="btn-primary" onClick={handleAcceptAll}>
              Alle akzeptieren
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
